'use client';

import { useState } from 'react'
import { Terminal, Code, ShieldAlert, CheckCircle } from 'lucide-react'

const codeExamples = {
  htmlForm: {
    html: `<form action="https://lenklyst.io/api/forms/YOUR_ENDPOINT_ID/submit" method="POST">
  <!-- Optional: Redirect users to a thank you page -->
  <input type="hidden" name="_redirect" value="https://yourwebsite.com/thanks" />

  <input type="email" name="email" placeholder="Enter your email" required />
  <input type="text" name="name" placeholder="Full Name" />
  
  <button type="submit">Submit</button>
</form>`
  },
  fetchApi: {
    javascript: `// You can submit JSON directly via Fetch or Axios
const submitForm = async (formData) => {
  const response = await fetch("https://lenklyst.io/api/forms/YOUR_ENDPOINT_ID/submit", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      data: {
        email: formData.email,
        name: formData.name
      }
    })
  });

  const result = await response.json();
  console.log('Submission successful:', result.success);
};`
  }
}

export default function HeadlessFormsDocsPage() {
  return (
    <div className="space-y-12">
      
      {/* Title */}
      <div className="space-y-4">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
          <Terminal className="w-3.5 h-3.5" /> API Specification
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">
          Headless Forms Guide
        </h1>
        <p className="text-white/50 font-light text-sm leading-relaxed max-w-3xl">
          Build completely custom forms on your own websites and applications. Submit data securely without exposing API keys by utilizing strict Domain Verification (CORS).
        </p>
      </div>

      <hr className="border-white/5" />

      {/* Security */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-primary" /> 1. Domain Verification Security
        </h2>
        <p className="text-sm text-white/50 leading-relaxed font-light">
          Instead of exposing private API keys in your client-side code, our Headless Forms use HTTP Origin headers to verify submissions. In your Headless Forms Dashboard, simply add the domains (e.g. <code>https://mywebsite.com</code>) that are permitted to submit data to the endpoint. Submissions from any other domain will be rejected with a <code>401 Unauthorized</code> response.
        </p>
      </div>

      <hr className="border-white/5" />

      {/* Endpoint: HTML Form */}
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" /> 2. Submit via HTML Form
          </h2>
          <div className="flex items-center gap-3 mt-2">
            <span className="px-2.5 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-mono font-bold uppercase rounded-md">POST</span>
            <span className="font-mono text-sm text-white/80">/api/forms/[endpoint_id]/submit</span>
          </div>
        </div>

        <p className="text-sm text-white/50 leading-relaxed font-light">
          You can use a standard HTML <code>&lt;form&gt;</code> tag to submit data. This requires absolutely zero Javascript. By including the <code>_redirect</code> hidden field, the server will automatically issue a <code>302 Redirect</code> to send the user back to your site after a successful submission.
        </p>

        {/* Code Example */}
        <div className="space-y-3">
          <pre className="p-5 bg-black border border-white/5 rounded-2xl overflow-x-auto font-mono text-xs text-emerald-400 leading-relaxed">
            {codeExamples.htmlForm.html}
          </pre>
        </div>
      </div>

      <hr className="border-white/5" />

      {/* Endpoint: Fetch API */}
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" /> 3. Submit via AJAX / Fetch
          </h2>
          <div className="flex items-center gap-3 mt-2">
            <span className="px-2.5 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-mono font-bold uppercase rounded-md">POST</span>
            <span className="font-mono text-sm text-white/80">/api/forms/[endpoint_id]/submit</span>
          </div>
        </div>

        <p className="text-sm text-white/50 leading-relaxed font-light">
          If you are building a Single Page Application (SPA) using React, Vue, or Angular, you can submit the data asynchronously. Pass your submission fields inside a <code>data</code> object.
        </p>

        {/* Code Example */}
        <div className="space-y-3">
          <pre className="p-5 bg-black border border-white/5 rounded-2xl overflow-x-auto font-mono text-xs text-blue-400 leading-relaxed">
            {codeExamples.fetchApi.javascript}
          </pre>
        </div>
      </div>

      <hr className="border-white/5" />

      {/* Verification footer */}
      <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
        <p className="text-xs text-white/70 leading-relaxed font-light">
          All submissions are automatically passed through our spam filter and rate limiter. You can view the raw JSON payloads inside your Headless Forms Dashboard Inbox.
        </p>
      </div>

    </div>
  )
}
